import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { productsAPI, stockAPI } from '../lib/api';
import api from '../lib/api';
import { formatNumber, getCategoryLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Clock, Package, MagnifyingGlass, FilePdf, Check, X, Upload } from '@phosphor-icons/react';
import { FileUp, AlertCircle } from 'lucide-react';

export default function StockOperations() {
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('entry');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: '',
    location: 'warehouse',
    notes: '',
    payment_type: 'cash',
  });

  // PDF Import State
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDate, setPdfDate] = useState(new Date().toISOString().split('T')[0]);
  const [pdfSupplier, setPdfSupplier] = useState('');
  const [pdfLocation, setPdfLocation] = useState('warehouse');
  const [pdfPaymentType, setPdfPaymentType] = useState('cash');
  const [pdfItems, setPdfItems] = useState([]);  // Kullanıcı düzenlemeleri için

  const PAYMENT_TYPES = [
    { code: 'cash', label: 'Nakit' },
    { code: 'credit_card', label: 'Kredi Kartı' },
    { code: 'bank_transfer', label: 'Havale/EFT' },
    { code: 'check', label: 'Çek' },
  ];

  // Filtrelenmiş ürünler
  const filteredProducts = useMemo(() => {
    if (!productSearchQuery.trim()) return products;
    return products.filter(p => 
      p.name.toLowerCase().includes(productSearchQuery.toLowerCase())
    );
  }, [products, productSearchQuery]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, transactionsRes] = await Promise.all([
        productsAPI.getAll(),
        stockAPI.getTransactions({ limit: 50 }),
      ]);
      setProducts(productsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.product_id || !formData.quantity) {
      toast.error('Lütfen ürün ve miktar seçin');
      return;
    }

    const data = {
      product_id: formData.product_id,
      quantity: parseFloat(formData.quantity),
      location: formData.location,
      notes: formData.notes || undefined,
      payment_type: formData.payment_type,
    };

    try {
      if (activeTab === 'entry') {
        await stockAPI.entry(data);
        toast.success('Stok girişi yapıldı');
      } else {
        await stockAPI.exit(data);
        toast.success('Stok çıkışı yapıldı');
      }
      
      setFormData({
        product_id: '',
        quantity: '',
        location: 'warehouse',
        notes: '',
        payment_type: 'cash',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const selectedProduct = products.find(p => p.id === formData.product_id);

  // PDF Import Functions
  const handlePdfFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Sadece PDF dosyaları kabul edilir');
      return;
    }
    
    setPdfFile(file);
    setPdfPreview(null);
    setPdfItems([]);
    setPdfLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/stock/preview-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPdfPreview(res.data);
      if (res.data.summary?.detected_supplier) {
        setPdfSupplier(res.data.summary.detected_supplier);
      }
      // Items'ı düzenlenebilir hale getir
      setPdfItems(res.data.items.map((item, idx) => ({
        ...item,
        idx,
        selected: item.matched_product_id !== null,
        create_if_new: false
      })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF okunamadı');
      setPdfFile(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const updatePdfItem = (idx, field, value) => {
    setPdfItems(prev => prev.map((item, i) => 
      i === idx ? { ...item, [field]: value } : item
    ));
  };

  const handlePdfImport = async () => {
    const selectedItems = pdfItems.filter(item => item.selected || item.create_if_new);
    
    if (selectedItems.length === 0) {
      toast.error('Lütfen en az bir ürün seçin');
      return;
    }
    
    setPdfLoading(true);
    
    try {
      const payload = {
        date: pdfDate,
        location: pdfLocation,
        supplier: pdfSupplier || null,
        payment_type: pdfPaymentType,
        items: selectedItems.map(item => ({
          product_id: item.matched_product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.unit_price,
          create_if_new: item.create_if_new
        }))
      };
      
      const res = await api.post('/stock/import-pdf', payload);
      
      toast.success(res.data.message);
      setIsPdfDialogOpen(false);
      resetPdfDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aktarım başarısız');
    } finally {
      setPdfLoading(false);
    }
  };

  const resetPdfDialog = () => {
    setPdfFile(null);
    setPdfPreview(null);
    setPdfItems([]);
    setPdfSupplier('');
    setPdfDate(new Date().toISOString().split('T')[0]);
    setPdfLocation('warehouse');
    setPdfPaymentType('cash');
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'entry':
        return <ArrowDown size={16} className="text-secondary" />;
      case 'exit':
      case 'sale_exit':
        return <ArrowUp size={16} className="text-destructive" />;
      default:
        return <Clock size={16} className="text-muted-foreground" />;
    }
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'entry':
        return 'Giriş';
      case 'exit':
        return 'Çıkış';
      case 'sale_exit':
        return 'Satış';
      case 'transfer':
        return 'Sevkiyat';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              Stok İşlemleri
            </h1>
            <p className="mt-2 text-muted-foreground">
              Stok giriş ve çıkış işlemlerinizi yönetin
            </p>
          </div>
          
          {/* PDF'den Aktar Butonu */}
          <Dialog open={isPdfDialogOpen} onOpenChange={(open) => { setIsPdfDialogOpen(open); if (!open) resetPdfDialog(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50">
                <FilePdf className="w-4 h-4 mr-2" />
                PDF'den Stok Girişi
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FilePdf className="w-5 h-5 text-blue-600" />
                  PDF Fatura ile Stok Girişi
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Dosya Seçimi */}
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfFileSelect}
                    className="hidden"
                    id="stock-pdf-upload"
                  />
                  <label htmlFor="stock-pdf-upload" className="cursor-pointer">
                    {pdfFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <FilePdf className="w-10 h-10 text-blue-500" />
                        <div className="text-left">
                          <p className="font-medium">{pdfFile.name}</p>
                          <p className="text-sm text-muted-foreground">{(pdfFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); setPdfFile(null); setPdfPreview(null); setPdfItems([]); }}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <FileUp className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                        <p className="font-medium">Fatura PDF'ini seçin</p>
                        <p className="text-sm text-muted-foreground">Ürünler otomatik eşleştirilecek</p>
                      </div>
                    )}
                  </label>
                </div>
                
                {/* Ayarlar */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Tarih</Label>
                    <Input type="date" value={pdfDate} onChange={(e) => setPdfDate(e.target.value)} />
                  </div>
                  <div>
                    <Label>Konum</Label>
                    <Select value={pdfLocation} onValueChange={setPdfLocation}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="warehouse">Depo</SelectItem>
                        <SelectItem value="store">Mağaza</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Ödeme</Label>
                    <Select value={pdfPaymentType} onValueChange={setPdfPaymentType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Nakit</SelectItem>
                        <SelectItem value="credit">Veresiye</SelectItem>
                        <SelectItem value="card">Kart</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tedarikçi</Label>
                    <Input value={pdfSupplier} onChange={(e) => setPdfSupplier(e.target.value)} placeholder="Opsiyonel" />
                  </div>
                </div>
                
                {pdfLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                    <span>PDF okunuyor...</span>
                  </div>
                )}
                
                {/* Önizleme */}
                {pdfPreview && !pdfLoading && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Check className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-700">{pdfPreview.summary.matched_count} eşleşti</span>
                        </div>
                        {pdfPreview.summary.new_count > 0 && (
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                            <span className="font-medium text-amber-700">{pdfPreview.summary.new_count} yeni ürün</span>
                          </div>
                        )}
                      </div>
                      <span className="text-lg font-bold text-blue-700">
                        ₺{pdfPreview.summary.total_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    
                    <div className="max-h-64 overflow-y-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">Seç</TableHead>
                            <TableHead>PDF Ürün</TableHead>
                            <TableHead>Eşleşen Ürün</TableHead>
                            <TableHead className="text-right">Miktar</TableHead>
                            <TableHead className="text-right">Birim Fiyat</TableHead>
                            <TableHead className="text-right">Toplam</TableHead>
                            <TableHead>Yeni?</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pdfItems.map((item, idx) => (
                            <TableRow key={idx} className={!item.matched_product_id && !item.create_if_new ? 'bg-amber-50' : ''}>
                              <TableCell>
                                <Checkbox
                                  checked={item.selected || item.create_if_new}
                                  onCheckedChange={(checked) => updatePdfItem(idx, 'selected', checked)}
                                  disabled={!item.matched_product_id && !item.create_if_new}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{item.product_name}</TableCell>
                              <TableCell>
                                {item.matched_product_name ? (
                                  <Badge className="bg-green-100 text-green-800">{item.matched_product_name}</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-amber-600 border-amber-300">Eşleşme yok</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                              <TableCell className="text-right">₺{item.unit_price.toLocaleString('tr-TR')}</TableCell>
                              <TableCell className="text-right font-bold">₺{item.total_amount.toLocaleString('tr-TR')}</TableCell>
                              <TableCell>
                                {!item.matched_product_id && (
                                  <Checkbox
                                    checked={item.create_if_new}
                                    onCheckedChange={(checked) => {
                                      updatePdfItem(idx, 'create_if_new', checked);
                                      if (checked) updatePdfItem(idx, 'selected', true);
                                    }}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-700">
                        <p className="font-medium">Not:</p>
                        <ul className="list-disc list-inside mt-1">
                          <li>Sadece seçili ürünler stoğa eklenecek</li>
                          <li>"Yeni?" sütununu işaretlerseniz eşleşmeyen ürünler yeni ürün olarak oluşturulur</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Butonlar */}
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setIsPdfDialogOpen(false); resetPdfDialog(); }}>İptal</Button>
                  <Button
                    onClick={handlePdfImport}
                    disabled={!pdfPreview || pdfLoading || pdfItems.filter(i => i.selected || i.create_if_new).length === 0}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {pdfItems.filter(i => i.selected || i.create_if_new).length} Ürünü Stoğa Ekle
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stock Operation Form */}
          <div className="border border-border bg-card p-6 rounded-xl">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full mb-6">
                <TabsTrigger value="entry" className="flex-1" data-testid="tab-entry">
                  <ArrowDown size={18} className="mr-2" />
                  Stok Girişi
                </TabsTrigger>
                <TabsTrigger value="exit" className="flex-1" data-testid="tab-exit">
                  <ArrowUp size={18} className="mr-2" />
                  Stok Çıkışı
                </TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Ürün Ara ve Seçin
                  </Label>
                  
                  {/* Ürün Arama */}
                  <div className="relative mb-2">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Ürün adı ile ara..."
                      value={productSearchQuery}
                      onChange={(e) => setProductSearchQuery(e.target.value)}
                      className="pl-10 h-10"
                      data-testid="stock-product-search"
                    />
                  </div>
                  
                  <Select
                    value={formData.product_id}
                    onValueChange={(value) => setFormData({ ...formData, product_id: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="stock-product-select">
                      <SelectValue placeholder="Ürün seçin" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {filteredProducts.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} ({getCategoryLabel(product.category)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {productSearchQuery && (
                    <p className="text-xs text-muted-foreground">
                      {filteredProducts.length} ürün bulundu
                    </p>
                  )}
                </div>

                {selectedProduct && (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Mevcut Stok:</p>
                    <div className="mt-2 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Depo</p>
                        <p className="font-data font-semibold">
                          {formatNumber(selectedProduct.warehouse_stock)} {selectedProduct.unit}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Mağaza</p>
                        <p className="font-data font-semibold">
                          {formatNumber(selectedProduct.store_stock)} {selectedProduct.unit}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Miktar
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      placeholder="0.00"
                      className="h-11"
                      data-testid="stock-quantity-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Konum
                    </Label>
                    <Select
                      value={formData.location}
                      onValueChange={(value) => setFormData({ ...formData, location: value })}
                    >
                      <SelectTrigger className="h-11" data-testid="stock-location-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="warehouse">Depo</SelectItem>
                        <SelectItem value="store">Mağaza</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Not (Opsiyonel)
                  </Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="İşlem hakkında not ekleyin..."
                    className="resize-none"
                    rows={3}
                    data-testid="stock-notes-input"
                  />
                </div>

                {activeTab === 'entry' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Ödeme Türü
                    </Label>
                    <Select
                      value={formData.payment_type}
                      onValueChange={(value) => setFormData({ ...formData, payment_type: value })}
                    >
                      <SelectTrigger className="h-11" data-testid="stock-payment-type-select">
                        <SelectValue placeholder="Ödeme türü seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_TYPES.map((pt) => (
                          <SelectItem key={pt.code} value={pt.code}>
                            {pt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  type="submit"
                  className={`w-full h-12 ${
                    activeTab === 'entry' 
                      ? 'bg-secondary hover:bg-secondary/90' 
                      : 'bg-destructive hover:bg-destructive/90'
                  }`}
                  data-testid="stock-submit"
                >
                  {activeTab === 'entry' ? (
                    <>
                      <ArrowDown size={20} className="mr-2" />
                      Stok Girişi Yap
                    </>
                  ) : (
                    <>
                      <ArrowUp size={20} className="mr-2" />
                      Stok Çıkışı Yap
                    </>
                  )}
                </Button>
              </form>
            </Tabs>
          </div>

          {/* Recent Transactions */}
          <div className="border border-border bg-card p-6 rounded-xl">
            <h2 className="font-heading text-xl font-semibold mb-4">Son İşlemler</h2>
            <div className="overflow-auto max-h-[500px]">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="border-b border-border">
                    <TableHead className="py-3">Ürün</TableHead>
                    <TableHead>Tür</TableHead>
                    <TableHead className="text-right">Miktar</TableHead>
                    <TableHead>Konum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-12">
                        <Package size={40} weight="duotone" className="mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground text-sm">Henüz işlem yok</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((t) => (
                      <TableRow key={t.id} className="table-row-hover border-b border-border/40">
                        <TableCell className="py-3">
                          <p className="font-medium text-sm">{t.product_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(t.created_at).toLocaleString('tr-TR')}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getTransactionIcon(t.transaction_type)}
                            <span className="text-sm">{getTransactionLabel(t.transaction_type)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-data">
                          {formatNumber(t.quantity)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {t.location === 'warehouse' ? 'Depo' : t.location === 'store' ? 'Mağaza' : t.location}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
